import { NPC_DATABASE } from '../../../../shared/NPCData';
import { MOB_REGISTRY } from '../../../../shared/MobRegistry';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import { TileType } from '../../../../shared/TileEditorTypes';
import { DEFAULT_HOTBAR_SLOTS, UNLOCKABLE_ABILITIES } from '../../../../shared/content/generated/hotbars.gen';
import type { QATemplate, QACategory, QAItem } from './qaTypes';

function makeItem(id: string, label: string): QAItem {
  return { id, label, status: 'unchecked', notes: '' };
}

// ── Infrastructure ──────────────────────────────────────

function buildInfrastructureCategory(): QACategory {
  return {
    id: 'infrastructure',
    label: 'Infrastructure',
    icon: '\u2699\uFE0F',
    items: [
      makeItem('server_starts', 'Colyseus server starts on :2567'),
      makeItem('client_starts', 'Vite client starts on :3001'),
      makeItem('room_join', 'Player can join GameRoom'),
      makeItem('db_connection', 'MongoDB connects (or memory fallback works)'),
      makeItem('toml_codegen', 'compile:content runs without errors'),
      makeItem('dashboard_green', 'Contract dashboard all green'),
      makeItem('type_check', 'tsc --noEmit passes'),
    ],
  };
}

// ── Data Loading ────────────────────────────────────────

function buildDataLoadingCategory(): QACategory {
  return {
    id: 'data_loading',
    label: 'Data Loading',
    icon: '\uD83D\uDCE6',
    items: [
      makeItem('toml_recipes', 'Recipes load from TOML (4 recipes)'),
      makeItem('toml_items', 'Items load from TOML (23 items)'),
      makeItem('toml_npcs', 'NPCs load from TOML (7 NPCs)'),
      makeItem('toml_mobs', 'Mobs load from TOML (10 mobs)'),
      makeItem('toml_quests', 'Quests load from TOML (9 quests)'),
      makeItem('toml_hotbars', 'Hotbars load from TOML (5 classes)'),
      makeItem('toml_loot', 'Loot tables load from TOML'),
      makeItem('class_configs', 'Player spawns with correct class stats'),
      makeItem('mob_spawn_data', 'EntitySpawner uses MOB_CONFIG data'),
    ],
  };
}

// ── HUD ─────────────────────────────────────────────────

function buildHUDCategory(): QACategory {
  return {
    id: 'hud',
    label: 'HUD & UI',
    icon: '\uD83D\uDCCA',
    items: [
      makeItem('health_bar', 'Health bar shows correct value'),
      makeItem('mana_bar', 'Mana bar shows correct value'),
      makeItem('xp_bar', 'XP bar shows correct value'),
      makeItem('player_info', 'Name, class, level display correctly'),
      makeItem('action_bar', 'Action bar renders 10 slots with icons'),
      makeItem('cooldown_overlay', 'Cooldown radial sweep animation'),
      makeItem('buff_bar', 'Buff bar shows active effects'),
      makeItem('chat_send', 'Chat sends messages'),
      makeItem('chat_receive', 'Chat receives messages from others'),
      makeItem('chat_filters', 'Chat filter tabs (all/combat/debug)'),
      makeItem('disconnect_overlay', 'Connection status on disconnect'),
      makeItem('inventory_window', 'Inventory opens, shows items'),
      makeItem('equipment_window', 'Equipment shows 8 slots'),
      makeItem('quest_window', 'Quest log shows active/completed'),
      makeItem('party_panel', 'Party panel shows members'),
      makeItem('trade_window', 'Trade dual-offer layout'),
      makeItem('crafting_window', 'Crafting recipes + ingredients'),
      makeItem('bank_window', 'Bank deposit/withdraw'),
      makeItem('shop_window', 'Shop items with prices'),
      makeItem('settings_window', 'Settings window renders'),
      makeItem('hud_zen_mode', 'HUD hides in zen mode'),
    ],
  };
}

// ── Entity Spawning ─────────────────────────────────────

function buildEntitySpawningCategory(): QACategory {
  return {
    id: 'entity_spawning',
    label: 'Entity Spawning',
    icon: '\uD83D\uDC3B',
    items: [
      ...Object.values(MOB_REGISTRY).map(mob =>
        makeItem(`mob_${mob.id}`, `${mob.name} spawns (Lv${mob.level}, ${mob.hp}hp)`)
      ),
      ...Object.values(NPC_DATABASE).map(npc =>
        makeItem(`npc_${npc.id}`, `${npc.name} spawns (${npc.role})`)
      ),
      makeItem('map_chests', 'Map chests spawn from level.entities'),
      makeItem('summon_stones', 'Summon stones spawn from teleporters'),
      makeItem('mob_respawn', 'Defeated mobs respawn after timer'),
    ],
  };
}

// ── Targeting ───────────────────────────────────────────

function buildTargetingCategory(): QACategory {
  return {
    id: 'targeting',
    label: 'Targeting',
    icon: '\uD83C\uDFAF',
    items: [
      makeItem('tab_cycle', 'Tab-cycle targeting works'),
      makeItem('target_frame', 'Target frame shows name/level/health'),
      makeItem('target_hide', 'Target frame hides on deselect'),
      makeItem('target_reticle', 'Visual reticle on targeted entity'),
      makeItem('dead_target_clear', 'Dead targets auto-cleared'),
      makeItem('target_range', 'Max target range enforced (1200px)'),
    ],
  };
}

// ── Combat ──────────────────────────────────────────────

function buildCombatCategory(): QACategory {
  return {
    id: 'combat',
    label: 'Combat',
    icon: '\u2694\uFE0F',
    items: [
      makeItem('attack_triggers', 'Attack input triggers server action'),
      makeItem('damage_numbers', 'Damage numbers appear on hit'),
      makeItem('target_health_dec', 'Target health decreases on hit'),
      makeItem('death_state', 'Entity death state on health=0'),
      makeItem('no_attack_dead', 'Cannot attack dead targets'),
      makeItem('no_attack_none', 'Cannot attack with no target'),
      makeItem('cooldowns', 'Cooldowns respected'),
      makeItem('gcd', 'GCD (500ms) works across abilities'),
      makeItem('melee_range', 'Melee range validated (150px)'),
      makeItem('ranged_range', 'Ranged range validated (850px)'),
      makeItem('invincibility', 'Invincibility flag respected'),
      makeItem('xp_on_kill', 'XP awarded on kill'),
      makeItem('level_up', 'Level up at xp >= level*100'),
      makeItem('crit_system', 'Critical hits with per-class multipliers'),
      makeItem('loot_drops', 'Loot drops from defeated mobs'),
      makeItem('combat_vfx', 'Combat feedback VFX (shake, hitstop)'),
      makeItem('level_up_vfx', 'Level-up VFX plays'),
    ],
  };
}

// ── Abilities ───────────────────────────────────────────

function buildAbilitiesCategory(): QACategory {
  return {
    id: 'abilities',
    label: 'Abilities',
    icon: '\u2728',
    items: [
      makeItem('55_registered', '55 abilities registered (5x10 + 5 autos)'),
      makeItem('auto_attacks', 'All 5 auto-attacks have damage formulas'),
      makeItem('behavior_types', '9 behavior types functional'),
      makeItem('balance_configs', 'Per-class balance configs wired'),
      makeItem('ability_designer', 'Ability Designer UI (6-tab editor)'),
      makeItem('hotbar_default', 'Hotbar populated from DefaultHotbars'),
      makeItem('projectile_shapes', 'Projectile shapes render correctly'),
    ],
  };
}

// ── Movement & Networking ───────────────────────────────

function buildNetworkingCategory(): QACategory {
  return {
    id: 'networking',
    label: 'Movement & Net',
    icon: '\uD83D\uDCE1',
    items: [
      makeItem('server_auth_60hz', 'Server-authoritative 60Hz physics'),
      makeItem('client_prediction', 'Client prediction with fixed timestep'),
      makeItem('error_correction', 'Smooth error correction (0.85 decay)'),
      makeItem('snap_threshold', 'SNAP_THRESHOLD guard (100px)'),
      makeItem('seq_dedup', 'Seq only increments on send'),
      makeItem('remote_interp', 'Remote player interpolation'),
      makeItem('teleporters', 'Teleporter system (same + cross map)'),
      makeItem('moving_platforms', 'Moving platform sync'),
    ],
  };
}

// ── Persistence & Services ──────────────────────────────

function buildServicesCategory(): QACategory {
  return {
    id: 'services',
    label: 'Services',
    icon: '\uD83D\uDDC4\uFE0F',
    items: [
      makeItem('char_save_mongo', 'Character save to MongoDB'),
      makeItem('dev_file_persist', 'Dev file persistence fallback'),
      makeItem('auto_save_60s', 'Auto-save every 60 seconds'),
      makeItem('party_system', 'Party create/join/leave'),
      makeItem('player_presence', 'Player presence tracking'),
      makeItem('guild_crud', 'Guild CRUD + roles + persistence'),
      makeItem('mount_system', 'Mount summon/mount/dismount'),
      makeItem('build_mode', 'Build mode draft + publish'),
    ],
  };
}

// ── Original categories (kept for backwards compat) ─────

function buildNPCCategory(): QACategory {
  return {
    id: 'npcs',
    label: 'NPCs',
    icon: '\uD83D\uDDE3\uFE0F',
    items: Object.values(NPC_DATABASE).map(npc =>
      makeItem(npc.id, `${npc.name} (${npc.role})`)
    ),
  };
}

function buildMobCategory(): QACategory {
  return {
    id: 'mobs',
    label: 'Mobs',
    icon: '\uD83D\uDC3B',
    items: Object.values(MOB_REGISTRY).map(mob =>
      makeItem(mob.id, `${mob.name} (Lv${mob.level})`)
    ),
  };
}

function buildTileCategory(): QACategory {
  const entries = Object.entries(TileType)
    .filter(([key, val]) => typeof val === 'number' && key !== 'EMPTY') as [string, number][];

  return {
    id: 'tiles',
    label: 'Tile Types',
    icon: '\uD83E\uDDF1',
    items: entries.map(([key]) =>
      makeItem(`tile_${key.toLowerCase()}`, key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' '))
    ),
  };
}

function buildItemCategory(): QACategory {
  return {
    id: 'items',
    label: 'Items',
    icon: '\uD83C\uDF92',
    items: Object.values(ITEM_DATABASE).map(item =>
      makeItem(item.id, `${item.name} (${item.type})`)
    ),
  };
}

function buildMapFeaturesCategory(): QACategory {
  return {
    id: 'map_features',
    label: 'Map Features',
    icon: '\uD83D\uDDFA\uFE0F',
    items: [
      makeItem('spawn_points', 'Spawn Points'),
      makeItem('teleporters', 'Teleporters'),
      makeItem('map_boundaries', 'Map Boundaries'),
      makeItem('camera_bounds', 'Camera Bounds'),
      makeItem('platforms', 'Platform Collision'),
      makeItem('background', 'Background Rendering'),
      makeItem('tile_rendering', 'Tile Rendering'),
      makeItem('surface_overlays', 'Surface Overlays'),
    ],
  };
}

function buildMobAICategory(): QACategory {
  return {
    id: 'mob_ai',
    label: 'Mob AI',
    icon: '\uD83E\uDDE0',
    items: [
      makeItem('ai_idle', 'Mobs idle when no players nearby'),
      makeItem('ai_wander', 'Mobs wander randomly near spawn'),
      makeItem('ai_aggro', 'Mobs aggro when player enters range (250px)'),
      makeItem('ai_aggro_damage', 'Mobs aggro immediately when damaged'),
      makeItem('ai_chase', 'Mobs chase toward target player'),
      makeItem('ai_chase_jump', 'Mobs jump when target is above'),
      makeItem('ai_attack', 'Mobs deal damage at melee range (60px)'),
      makeItem('ai_attack_interval', 'Mobs attack every 1.5s'),
      makeItem('ai_leash', 'Mobs leash when >600px from spawn'),
      makeItem('ai_return', 'Mobs walk home after leashing'),
      makeItem('ai_death', 'Mobs stop on death, wait for respawn'),
      makeItem('ai_respawn', 'Mobs respawn at spawn point after timer'),
      makeItem('ai_schema_sync', 'aiState + targetId sync to clients'),
      makeItem('ai_multi_mob', 'Multiple mobs act independently'),
    ],
  };
}

function buildNPCInteractionCategory(): QACategory {
  return {
    id: 'npc_interaction',
    label: 'NPC Interaction',
    icon: '\uD83D\uDCAC',
    items: [
      makeItem('npc_talk', 'talkToNPC returns dialogue + role'),
      makeItem('npc_range_check', 'NPC interaction range-checked (120px)'),
      makeItem('npc_shop_items', 'Shop NPCs return item list with prices'),
      makeItem('npc_buy_gold', 'buyItem deducts gold correctly'),
      makeItem('npc_buy_inventory', 'buyItem adds to inventory'),
      makeItem('npc_buy_stack', 'buyItem respects maxStack'),
      makeItem('npc_buy_no_gold', 'buyItem rejects if not enough gold'),
      makeItem('npc_sell', 'sellItem gives 40% of buy price'),
      makeItem('npc_sell_remove', 'sellItem removes from inventory'),
      makeItem('npc_quest_giver', 'Quest NPCs list available quests'),
    ],
  };
}

// ── VFX System ─────────────────────────────────────────

function buildVFXSystemCategory(): QACategory {
  return {
    id: 'vfx_system',
    label: 'VFX System',
    icon: '\u2728',
    items: [
      makeItem('vfx_registry', 'VFXEffectRegistry has 60+ effects'),
      makeItem('vfx_particle_presets', 'Particle presets registered (8)'),
      makeItem('vfx_procedural', 'Procedural VFX registered (4)'),
      makeItem('vfx_budget_cap', 'Budget manager caps at 150 shapes'),
      makeItem('vfx_melee_swing', 'Melee swing arc renders on BasicStrike'),
      makeItem('vfx_hitscan_beam', 'Hitscan beam renders on cast'),
      makeItem('vfx_summon_indicator', 'Summon placement indicator shows'),
      makeItem('vfx_screen_shake', 'Screen shake works via event'),
      makeItem('vfx_screen_flash', 'Screen flash works via event'),
      makeItem('vfx_trail', 'withTrail() generates trail VFX'),
      makeItem('vfx_impact', 'withImpactEffect() renders on hit'),
      makeItem('vfx_cast', 'withCastEffect() renders on cast'),
      makeItem('vfx_aura', 'withAura() shows persistent aura'),
    ],
  };
}

// ── Ability Modifiers ──────────────────────────────────

function buildAbilityModifiersCategory(): QACategory {
  return {
    id: 'ability_modifiers',
    label: 'Ability Modifiers',
    icon: '\uD83D\uDD27',
    items: [
      makeItem('mod_pierce', 'Pierce: projectile passes through targets'),
      makeItem('mod_bounce', 'Bounce: redirects to nearest target'),
      makeItem('mod_chain', 'Chain: spawns new projectile to next target'),
      makeItem('mod_split', 'Split: child projectiles at angles'),
      makeItem('mod_aoe_impact', 'AoE on impact: area_hit at collision'),
      makeItem('mod_onhit_poison', 'On-hit poison applies DoT'),
      makeItem('mod_onhit_burn', 'On-hit burn applies fire DoT'),
      makeItem('mod_onhit_slow', 'On-hit slow reduces speed'),
      makeItem('mod_onhit_lifesteal', 'Lifesteal heals attacker'),
      makeItem('mod_onhit_knockback', 'Knockback pushes target'),
      makeItem('mod_element_fire', 'Fire element: auto colors + burn'),
      makeItem('mod_element_frost', 'Frost element: auto colors + slow'),
      makeItem('mod_element_shadow', 'Shadow element: auto colors + poison'),
      makeItem('mod_conditional', 'Conditional: bonus dmg below 50% HP'),
      makeItem('mod_combo_chain', 'Combo chain: chainInto opens window'),
    ],
  };
}

// ── Sprint QA (2026-03-18 — Ability Contracts + DebugFCT) ──

function buildSprintActionBarCategory(): QACategory {
  return {
    id: 'sprint_actionbar',
    label: 'Action Bar & Tooltips',
    icon: '\uD83C\uDFAE',
    items: [
      makeItem('s_tooltip_hover', 'Hover slot 2 — tooltip shows ability name + mana + cooldown'),
      makeItem('s_tooltip_position', 'Tooltip follows cursor, not clipped by screen edge'),
      makeItem('s_tooltip_hide', 'Tooltip hides when mouse leaves slot'),
      makeItem('s_cooldown_sweep', 'Cast ability — radial sweep overlay appears on slot'),
      makeItem('s_cooldown_timer', 'Cooldown countdown number visible on slot'),
      makeItem('s_cooldown_clears', 'Cooldown overlay clears when CD expires'),
      makeItem('s_gcd_visual', 'GCD dims all slots briefly after any cast'),
      makeItem('s_mana_grayout', 'Slots gray out when mana insufficient'),
    ],
  };
}

function buildSprintCastingCategory(): QACategory {
  const classes = ['warrior', 'mage', 'hunter', 'paladin', 'rogue', 'shaman', 'priest', 'warlock', 'druid'];
  const items: QAItem[] = [];
  for (const cls of classes) {
    items.push(makeItem(`s_cast_${cls}_auto`, `${cls}: auto-attack deals damage`));
    items.push(makeItem(`s_cast_${cls}_slot2`, `${cls}: slot 2 ability fires + VFX`));
    items.push(makeItem(`s_cast_${cls}_slot3`, `${cls}: slot 3 ability fires + VFX`));
    items.push(makeItem(`s_cast_${cls}_slot5`, `${cls}: slot 5 ability fires + VFX`));
    items.push(makeItem(`s_cast_${cls}_mana`, `${cls}: mana deducted on cast`));
  }
  return { id: 'sprint_casting', label: 'Ability Casting (5 classes)', icon: '\u26A1', items };
}

function buildSprintEquipmentCategory(): QACategory {
  return {
    id: 'sprint_equipment',
    label: 'Equipment Pipeline',
    icon: '\uD83D\uDEE1\uFE0F',
    items: [
      makeItem('s_equip_weapon', 'Equip weapon — attack stat increases'),
      makeItem('s_equip_hat', 'Equip hat — defense stat increases'),
      makeItem('s_equip_top', 'Equip top — defense stat increases'),
      makeItem('s_equip_bottom', 'Equip bottom — stats change'),
      makeItem('s_equip_shoes', 'Equip shoes — stats change'),
      makeItem('s_equip_gloves', 'Equip gloves — stats change'),
      makeItem('s_equip_cape', 'Equip cape — stats change'),
      makeItem('s_equip_accessory', 'Equip accessory — stats change'),
      makeItem('s_unequip', 'Unequip item — stats revert'),
      makeItem('s_equip_visual', 'Weapon visual changes on equip'),
    ],
  };
}

function buildSprintLootCategory(): QACategory {
  return {
    id: 'sprint_loot',
    label: 'Loot & Drops',
    icon: '\uD83D\uDCB0',
    items: [
      makeItem('s_loot_gold_drop', 'Kill pig — gold pile appears on ground'),
      makeItem('s_loot_item_drop', 'Kill mob — item drop appears'),
      makeItem('s_loot_pickup_gold', 'Walk over gold — gold added to inventory'),
      makeItem('s_loot_pickup_item', 'Walk over item — item added to inventory'),
      makeItem('s_loot_despawn', 'Uncollected loot despawns after timer'),
      makeItem('s_loot_rarity_color', 'Drop rarity affects name color'),
    ],
  };
}

function buildSprintCombatFeedbackCategory(): QACategory {
  return {
    id: 'sprint_combat_feedback',
    label: 'Combat Feedback',
    icon: '\uD83D\uDCA5',
    items: [
      makeItem('s_fb_damage_numbers', 'Damage numbers float up from mob'),
      makeItem('s_fb_crit_color', 'Critical hits show orange/gold numbers'),
      makeItem('s_fb_screen_shake', 'Heavy hits cause screen shake'),
      makeItem('s_fb_hitstop', 'Hitstop pause on heavy hits'),
      makeItem('s_fb_element_color', 'Fire=red, frost=blue, holy=gold damage colors'),
      makeItem('s_fb_heal_numbers', 'Healing shows green numbers'),
      makeItem('s_fb_xp_text', 'XP gain shows blue "+X XP" text'),
    ],
  };
}

function buildSprintDebugFCTCategory(): QACategory {
  return {
    id: 'sprint_debug_fct',
    label: 'DebugFCT Channels',
    icon: '\uD83D\uDD0D',
    items: [
      makeItem('s_dfct_cast_success', '[cast] Green text on successful cast (name + mana + CD)'),
      makeItem('s_dfct_cast_fail_gcd', '[cast] Red text on GCD block'),
      makeItem('s_dfct_cast_fail_cd', '[cast] Red text on cooldown active'),
      makeItem('s_dfct_cast_fail_mana', '[cast] Red text on insufficient mana'),
      makeItem('s_dfct_cast_slot_resolve', '[cast] Blue text showing slot -> ability name'),
      makeItem('s_dfct_vfx_spawn', '[vfx] Blue text on VFX effect spawn'),
      makeItem('s_dfct_vfx_persist', '[vfx] Blue text on persistent effect start'),
      makeItem('s_dfct_combat_send', '[combat] Blue text on damage sent to server'),
      makeItem('s_dfct_combat_echo', '[combat] Green text on server damage echo'),
      makeItem('s_dfct_combat_player_hit', '[combat] Red text when player takes damage'),
      makeItem('s_dfct_net_outbound', '[network] Blue text on outbound message'),
      makeItem('s_dfct_net_inbound', '[network] Blue text on inbound message'),
      makeItem('s_dfct_all_off', 'All channels OFF -> zero debug text'),
    ],
  };
}

function buildSprintBotAICategory(): QACategory {
  return {
    id: 'sprint_bot_ai',
    label: 'Bot AI System',
    icon: '\uD83E\uDD16',
    items: [
      makeItem('s_bot_spawn', '/bot spawn warrior 2 — bots appear'),
      makeItem('s_bot_move', 'Bots wander near spawn point'),
      makeItem('s_bot_aggressive', '/bot aggressive — bots chase and attack mobs'),
      makeItem('s_bot_damage', 'Bots deal damage (damage numbers appear)'),
      makeItem('s_bot_passive', '/bot passive — bots stop fighting'),
      makeItem('s_bot_despawn', '/bot despawn all — bots removed'),
      makeItem('s_bot_schema_sync', 'Bots visible to other players (2nd tab)'),
    ],
  };
}

function buildSprintDashboardCategory(): QACategory {
  return {
    id: 'sprint_dashboards',
    label: 'Contract Dashboard',
    icon: '\uD83D\uDCCB',
    items: [
      makeItem('s_dash_open', 'Contract Dashboard opens without error'),
      makeItem('s_dash_toml', 'TOML Pipeline: 7/7 green'),
      makeItem('s_dash_hotbar', 'Hotbar Contracts: 6/6 green'),
      makeItem('s_dash_equipment', 'Equipment Contracts: 5/5 green (was 4/5)'),
      makeItem('s_dash_loot', 'Loot System: 5/5 green'),
      makeItem('s_dash_combat_fb', 'Combat Feedback: 3/3 green'),
      makeItem('s_dash_server', 'Server Contracts: 9/9 green'),
      makeItem('s_dash_abilities', 'Ability Dashboard: 56/56 abilities all checks green'),
    ],
  };
}

function buildSprintMultiplayerCategory(): QACategory {
  return {
    id: 'sprint_multiplayer',
    label: 'Multiplayer Sync',
    icon: '\uD83C\uDF10',
    items: [
      makeItem('s_mp_movement', 'Open 2 tabs — movement syncs between players'),
      makeItem('s_mp_projectiles', 'Player 1 fires arrow — Player 2 sees projectile'),
      makeItem('s_mp_damage_numbers', 'Player 1 hits mob — Player 2 sees damage numbers'),
      makeItem('s_mp_ability_vfx', 'Player 1 casts — Player 2 sees VFX'),
      makeItem('s_mp_death_sync', 'Mob death visible on both clients'),
      makeItem('s_mp_chat', 'Chat messages appear on both clients'),
    ],
  };
}

// ── Per-Class Ability QA (auto-generated from hotbar data) ──

const CLASS_LABELS: Record<string, { label: string; icon: string }> = {
  warrior:  { label: 'Warrior',  icon: '\u2694\uFE0F' },
  mage:     { label: 'Mage',     icon: '\uD83E\uDDD9' },
  hunter:   { label: 'Hunter',   icon: '\uD83C\uDFF9' },
  paladin:  { label: 'Paladin',  icon: '\uD83D\uDEE1\uFE0F' },
  rogue: { label: 'Rogue', icon: '\uD83D\uDDE1\uFE0F' },
};

function buildClassAbilityCategory(className: string): QACategory {
  const meta = CLASS_LABELS[className] || { label: className, icon: '\u2728' };
  const hotbar = DEFAULT_HOTBAR_SLOTS[className] || [];
  const unlockable = UNLOCKABLE_ABILITIES[className] || [];

  const items: QAItem[] = hotbar.map(slot =>
    makeItem(
      `ability_${className}_${slot.abilityId}`,
      `[${slot.key}] ${slot.abilityId} -- cast + VFX + damage`
    )
  );

  for (const abilityId of unlockable) {
    items.push(makeItem(
      `ability_${className}_${abilityId}`,
      `[unlock] ${abilityId} -- cast + VFX + damage`
    ));
  }

  return { id: `ability_qa_${className}`, label: `${meta.icon} ${meta.label}`, icon: meta.icon, items };
}

export function buildAbilityQATemplate(): QATemplate {
  return {
    id: 'ability_qa_all_classes',
    name: 'Ability QA (All Classes)',
    categories: Object.keys(CLASS_LABELS).map(buildClassAbilityCategory),
  };
}

// ── Templates ───────────────────────────────────────────

export function buildFullGameQATemplate(): QATemplate {
  return {
    id: 'full_game_qa',
    name: 'Full Game QA',
    categories: [
      buildInfrastructureCategory(),
      buildDataLoadingCategory(),
      buildHUDCategory(),
      buildEntitySpawningCategory(),
      buildTargetingCategory(),
      buildCombatCategory(),
      buildAbilitiesCategory(),
      buildNetworkingCategory(),
      buildServicesCategory(),
      buildMobAICategory(),
      buildNPCInteractionCategory(),
      buildVFXSystemCategory(),
      buildAbilityModifiersCategory(),
      buildNPCCategory(),
      buildMobCategory(),
      buildTileCategory(),
      buildItemCategory(),
      buildMapFeaturesCategory(),
    ],
  };
}

export function buildSprintQATemplate(): QATemplate {
  return {
    id: 'sprint_qa_2026_03_18',
    name: 'Sprint QA — Contracts + DebugFCT',
    categories: [
      buildSprintActionBarCategory(),
      buildSprintCastingCategory(),
      buildSprintEquipmentCategory(),
      buildSprintLootCategory(),
      buildSprintCombatFeedbackCategory(),
      buildSprintDebugFCTCategory(),
      buildSprintBotAICategory(),
      buildSprintDashboardCategory(),
      buildSprintMultiplayerCategory(),
    ],
  };
}

export function buildSystemsQATemplate(): QATemplate {
  return {
    id: 'systems_qa',
    name: 'MMORPG Systems QA',
    categories: [
      buildInfrastructureCategory(),
      buildDataLoadingCategory(),
      buildHUDCategory(),
      buildEntitySpawningCategory(),
      buildTargetingCategory(),
      buildCombatCategory(),
      buildAbilitiesCategory(),
      buildNetworkingCategory(),
      buildServicesCategory(),
    ],
  };
}

// ── Template registry (QAPanel selector order) ──────────

export const TEMPLATES: { label: string; build: () => QATemplate }[] = [
  { label: 'Sprint QA', build: buildSprintQATemplate },
  { label: 'Ability QA', build: buildAbilityQATemplate },
  { label: 'Full Game QA', build: buildFullGameQATemplate },
  { label: 'Systems QA', build: buildSystemsQATemplate },
];
